// © Microsoft Corporation. All rights reserved.
import { canUseDOM, getLocationHostname, getLocationSearch } from "@msnews/isomorphic-util";
import { getAudienceModeFromCookies } from "@msnews/cookie-utils";
import { getFlightsFilterPrefix, getPeregrineFlights } from "@msnews/experimentation-helpers";
import { isChinaCompliance } from "@msnews/fundamentals";
import { tryParseJson } from "@msnews/core-helper";
/**
 * Initialize the inline-head-common module logic.
 * This currently includes the following:
 * - Fetching the CRS call early
 * @param options - Options for the inlineHead initialization call
 */
export function init(initOptions = {}) {
    const result = {};
    try {
        if (!canUseDOM()) {
            return result;
        }
        const clientSettings = parseClientSettings();
        let crsAppType = clientSettings.apptype?.toLocaleLowerCase();
        const crsOptions = initOptions.crsOptions || {};
        // Only turn on early CRS call for Prong 2 and MSN HP. Due to shared init logic for app types
        // Until universal shipping - All other app types will have to opt-in to early CRS call via flights or update similarly
        if (crsAppType === "winwidgets" || crsAppType === "homepage" || crsAppType === "travel") {
            crsOptions.crsPrefetch = true;
        }
        // Turn on early CRS call if it's set by the host page for widgets.
        if (clientSettings.widgetAttributes?.clientPerf?.earlyCRSCall) {
            crsOptions.crsPrefetch = true;
        }
        // flight name for edgeChromium exceeds the 20 characters limit
        if (crsAppType === "edgechromium") {
            crsAppType = "edgechr";
        }
        // shouldUsePWOnlyFlights is typically set by the host page in the widgets page.
        // Widget initializer adds WidgetAttributes exist in the host into client settings for appEnvironment to consume.
        const shouldUsePWOnlyFlights = !!clientSettings.widgetAttributes?.clientPerf?.shouldUsePWOnlyFlights;
        const { all, prg } = parseFlights(shouldUsePWOnlyFlights, crsAppType);
        // Early CRS call is currently enabled based on the flight
        // and the feature switch enablement by each canvas
        const earlyCRSCall = crsOptions.crsPrefetch || (all.includes("inlinecrscall") || all.includes(`icrscall-${crsAppType}`));
        if (earlyCRSCall) {
            fetchCRS(clientSettings, prg, crsOptions);
            result.inlineCRS = true;
        }
    }
    catch (ex) {
        // eslint-disable-next-line no-console
        console.error("InlineHeadCommon: " + ex);
    }
    return result;
}
/**
 * Prepares the request object for the CRS call and fetch the response.
 * The method will attempt to fetch from assets.msn.com first, and then fallback
 * to www.msn.com if the first attempt fails. The resulting promise will be cached
 * in the global scope for the ConfigService to consume later when requested by the page.
 * @param clientSettings - Parsed version of the client settings value from the document
 * @param prgExperiments - The list of `prg-` prefixed experiments to be passed to the CRS call
 * @param crsOptions - Options to modify the fetch sequence
 */
function fetchCRS(clientSettings, prgExperiments, crsOptions) {
    const audienceMode = getAudienceModeFromCookies(document.cookie, clientSettings);
    const { apptype, browser, domain: pcsDomain, bundleInfo: { v: bundleVersion } = {}, deviceFormFactor, locale: { content, display } = {}, ocid, os, modes: pageModes, pagetype: pageType, servicesEndpoints: { crs } = {} } = clientSettings;
    // for widget scenarios we don't want to use the PCS provided domain
    // as the host page domain can be completely different than the PCS call,
    // so we use the current host in that case
    const domain = (tryGetWidgetElement() ? getLocationHostname() : pcsDomain) || "";
    if (!apptype || !bundleVersion || !crs) {
        return;
    }
    // Audience mode can be overriden by the client, hence need to set it here.
    // Order is important here, as it will compare with the one using in the config service.
    const modes = {
        audienceMode,
        uxMode: pageModes?.uxMode
    };
    const currentRequestTargetScope = {
        audienceMode,
        browser,
        deviceFormFactor,
        domain,
        locale: { content, display },
        ocid,
        os,
        modes,
        platform: "web",
        pageType,
        pageExperiments: prgExperiments
    };
    const params = new URLSearchParams([
        ["expType", "AppConfig"],
        ["expInstance", "default"],
        ["apptype", apptype],
        ["v", bundleVersion]
        // Note we don't add the target scope object here as the URLSearchParams adds a lot of
        // escape characters that bloats the payload. Instead we add it as raw JSON string below
    ]);
    // If url has dev only query string params, send this dev/test traffic to int1 environment instead of prod.
    const locationSearch = getLocationSearch();
    const devQueryParam = locationSearch.includes("?item=") || locationSearch.includes("&item=")
        ? "&citem=dev:true&instance=Int1"
        : "";
    const crsPathAndQuery = `${crs.path}${crs.v}/config/?${params}&targetScope=${JSON.stringify(currentRequestTargetScope)}${devQueryParam}`;
    const fetchHelper = (domain) => {
        const url = new URL(`https://${domain}${crsPathAndQuery}`);
        const { timeout = 5000 } = crsOptions;
        // this is checked in the early CRS promise in ConfigService.ts
        window.crsRequestData = { url: url.toString() };
        let abortSignal;
        if (timeout >= 0) {
            const ctrl = new AbortController();
            setTimeout(() => ctrl.abort(), timeout);
            abortSignal = ctrl.signal;
        }
        return fetch(url, {
            signal: abortSignal
        });
    };
    const compliantSuperMsnDomain = `msn.${isChinaCompliance(location, clientSettings) ? "cn" : "com"}`;
    // this value is often empty, but if it's not, we should use it as the domain
    const { domain: serverSpecifiedCrsOrigin } = crs;
    const serverSpecifiedCrsDomain = serverSpecifiedCrsOrigin && new URL(serverSpecifiedCrsOrigin).host;
    // enable existing path in ConfigService.ts to fetch result from global promise variable
    let crsPromise = fetchHelper(serverSpecifiedCrsDomain || `assets.${compliantSuperMsnDomain}`);
    if (!crsOptions.disableFallback) {
        crsPromise = crsPromise.catch(() => {
            // only *.msn.com should fallback to the host page domain, otherwise for things like bing.com, it should
            // fallback to www.msn.com
            const { hostname } = location;
            const fallbackDomain = hostname.match(/\.msn\.(com|cn)$/)
                ? hostname
                : `www.${compliantSuperMsnDomain}`;
            return fetchHelper(fallbackDomain);
        });
    }
    if (crsOptions.silenceUnhandledError) {
        crsPromise = crsPromise.catch((err) => {
            // eslint-disable-next-line no-console
            console.error("InlineHeadCommon: CRS fetch error in silence", err);
            return undefined;
        });
    }
    window.crsPromise = crsPromise;
}
/**
 * Parses the flights list from the document -- supports both normal and widget modes.
 * @param shouldUsePWOnlyFlights - Flag to keep only prg-pw- flights
 * @param appType - App type
 * @returns
 */
export function parseFlights(shouldUsePWOnlyFlights = false, appType = "") {
    const allFlights = tryGetFlights(getPageDatasetValue("info")) || [];
    // When data-canvas-info is present, we use instead of getting prg- flights from all flights.
    let prgFlights = tryGetFlights(getPageDatasetValue("canvasInfo"));
    if (!prgFlights) {
        const prgFilterPrefix = getFlightsFilterPrefix(allFlights, shouldUsePWOnlyFlights, appType);
        prgFlights = getPeregrineFlights(allFlights, prgFilterPrefix);
    }
    return {
        all: allFlights,
        prg: prgFlights
    };
}
/**
 * Parses the client settings value from the document -- supports both normal and widget modes.
 * @returns The core client settings object
 */
function parseClientSettings() {
    return tryParseJson(getPageDatasetValue("clientSettings")) || {};
}
/**
 * Helper to get one of the well-known dataset values from the document.
 * @param key - The key to get from the dataset
 * @returns The value of the dataset key, or undefined if not found
 */
function getPageDatasetValue(key) {
    // Note that we look at the head dataset first because that's the majority of the cases.
    // We only query for the widget scenario if the head dataset doesn't have the value.
    return document.head.dataset[key]
        || tryGetWidgetElement()?.dataset[key];
}
/**
 * Cached widget element value from a previous query -- since we query for both
 * `clientSettings` and `info` values, caching this will save on another DOM query
 */
let widgetElement;
/**
 * Queries the document for the widget element and caches the result.
 * @returns The widget element, or null if not found
 */
function tryGetWidgetElement() {
    if (widgetElement === undefined) {
        widgetElement = document.getElementsByClassName("peregrine-widget-settings")[0] || null;
    }
    return widgetElement;
}
/** Helper to reset internal state for UTs */
export function _resetInlineHeadCommon() {
    widgetElement = undefined;
}
/**
 * Parse head dataset and get the flights in lower case and sorted order.
 * @param key info or canvasInfo
 * @returns Array of flights present in the dataset
 */
function tryGetFlights(flightString) {
    if (!flightString || !flightString.startsWith("f:")) {
        // Return undefined if the dataset is not present or empty.
        // Example: data-canvas-info or data-canvas-info: "" or data-canvas-info: "string without f:"
        return;
    }
    const reduced = ((flightString.match(/f:\s*([^;]+)/i)) || [])[1];
    if (!reduced) {
        // Return empty array if the flight string is empty.
        // Example: data-canvas-info: "f:;"
        // This is a valid case where the flight string is empty.
        return [];
    }
    // Example: data-canvas-info: "f:flight1,flight2,flight3;"
    // Returns ["flight1", "flight2", "flight3"]
    return reduced.toLowerCase().split(",")?.sort();
}
//# sourceMappingURL=InlineHeadCommon.js.map